import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';

export type StatTone = 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CONFIG: Record<
  StatTone,
  {
    iconBox: string;
    blob: string;
    dot: string;
    textColor: string;
  }
> = {
  primary: {
    iconBox: 'bg-[#EDE9FE] text-[#6825F5]',
    blob: 'from-purple-300/40 via-violet-200/25 to-transparent',
    dot: 'bg-[#8B5CF6]',
    textColor: 'text-[#6825F5]',
  },
  success: {
    iconBox: 'bg-[#D1FAE5] text-[#059669]',
    blob: 'from-emerald-300/40 via-teal-200/25 to-transparent',
    dot: 'bg-[#10B981]',
    textColor: 'text-[#059669]',
  },
  info: {
    iconBox: 'bg-[#DBEAFE] text-[#2563EB]',
    blob: 'from-blue-300/40 via-sky-200/25 to-transparent',
    dot: 'bg-[#3B82F6]',
    textColor: 'text-[#2563EB]',
  },
  warning: {
    iconBox: 'bg-[#FEF3C7] text-[#D97706]',
    blob: 'from-amber-300/40 via-orange-200/25 to-transparent',
    dot: 'bg-[#F59E0B]',
    textColor: 'text-[#D97706]',
  },
  danger: {
    iconBox: 'bg-[#FEE2E2] text-[#DC2626]',
    blob: 'from-rose-300/40 via-red-200/25 to-transparent',
    dot: 'bg-[#EF4444]',
    textColor: 'text-[#DC2626]',
  },
};

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  tone?: StatTone;
  index?: number;
  sublabel?: ReactNode;
  className?: string;
}

export function StatTile({ icon: Icon, label, value, tone = 'primary', index = 0, sublabel, className }: StatTileProps) {
  const cfg = TONE_CONFIG[tone] || TONE_CONFIG.primary;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.05 }}
      whileHover={{ y: -2 }}
      className="h-full"
    >
      <Card
        className={`group relative overflow-hidden rounded-2xl border border-[#E8E8F5] bg-white p-5 shadow-sm hover:shadow-md transition-all duration-200 h-full flex items-start gap-4 ${className ?? ''}`}
      >
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-transform duration-200 group-hover:scale-105 ${cfg.iconBox}`}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1 z-10">
          <p className="text-2xl font-bold tracking-tight text-[#11183F] leading-tight">{value}</p>
          <p className="mt-0.5 truncate text-xs font-medium text-[#747C9C]">{label}</p>
          {sublabel && (
            <div className="flex items-center gap-1.5 mt-2">
              <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
              <span className={`text-[11px] font-medium ${cfg.textColor}`}>{sublabel}</span>
            </div>
          )}
        </div>

        {/* Decorative corner glow blob */}
        <div
          className={`pointer-events-none absolute -bottom-8 -right-8 h-28 w-28 rounded-full bg-gradient-to-tl ${cfg.blob} blur-xl`}
          aria-hidden="true"
        />
      </Card>
    </motion.div>
  );
}

